import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Edit, LogOut, Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const LOGO_CHOCO = "https://customer-assets.emergentagent.com/job_6fa72c44-1c40-4a4f-af1b-3eb11820d1a2/artifacts/gxcr3jm2_Logo%20chocolat.png";

function useAdminToken() {
  const [token, setToken] = useState(() => localStorage.getItem("ew_admin_token") || "");
  const save = (t) => { localStorage.setItem("ew_admin_token", t); setToken(t); };
  const clear = () => { localStorage.removeItem("ew_admin_token"); setToken(""); };
  return { token, save, clear };
}

export default function Admin() {
  const { token, save, clear } = useAdminToken();
  const [tab, setTab] = useState("gallery");
  const [showPw, setShowPw] = useState(false);

  // Auth
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  // Gallery
  const [gallery, setGallery] = useState([]);
  const [newPhoto, setNewPhoto] = useState({ url: "", title: "", category: "galerie" });

  // Blog
  const [posts, setPosts] = useState([]);
  const [editPost, setEditPost] = useState(null);
  const [newPost, setNewPost] = useState({ title: "", excerpt: "", content: "", image_url: "", category: "Conseils", published: true });
  const [showNewPost, setShowNewPost] = useState(false);

  // Contacts
  const [contacts, setContacts] = useState([]);

  const headers = { "Content-Type": "application/json", "x-admin-token": token };

  const login = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        save(data.token);
        toast.success("Connexion réussie");
      } else {
        toast.error("Mot de passe incorrect");
      }
    } catch {
      toast.error("Erreur de connexion");
    } finally {
      setAuthLoading(false);
    }
  };

  const loadGallery = async () => {
    const res = await fetch(`${API}/gallery`);
    const data = await res.json();
    setGallery(data);
  };

  const loadPosts = async () => {
    const res = await fetch(`${API}/admin/blog`, { headers: { "x-admin-token": token } });
    const data = await res.json();
    setPosts(Array.isArray(data) ? data : []);
  };

  const loadContacts = async () => {
    const res = await fetch(`${API}/admin/contacts`, { headers: { "x-admin-token": token } });
    const data = await res.json();
    setContacts(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    if (token) {
      loadGallery();
      loadPosts();
      loadContacts();
    }
  }, [token]);

  const addPhoto = async (e) => {
    e.preventDefault();
    if (!newPhoto.url || !newPhoto.title) return toast.error("URL et titre requis");
    const res = await fetch(`${API}/gallery`, {
      method: "POST", headers,
      body: JSON.stringify(newPhoto),
    });
    if (res.ok) {
      toast.success("Photo ajoutée");
      setNewPhoto({ url: "", title: "", category: "galerie" });
      loadGallery();
    } else {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Supprimer cette photo ?")) return;
    const res = await fetch(`${API}/gallery/${id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Photo supprimée"); loadGallery(); }
    else toast.error("Erreur de suppression");
  };

  const savePost = async (e) => {
    e.preventDefault();
    const url = editPost ? `${API}/blog/${editPost.id}` : `${API}/blog`;
    const method = editPost ? "PUT" : "POST";
    const body = editPost
      ? JSON.stringify({ title: editPost.title, excerpt: editPost.excerpt, content: editPost.content, image_url: editPost.image_url, category: editPost.category, published: editPost.published })
      : JSON.stringify(newPost);
    const res = await fetch(url, { method, headers, body });
    if (res.ok) {
      toast.success(editPost ? "Article mis à jour" : "Article créé");
      setEditPost(null);
      setShowNewPost(false);
      setNewPost({ title: "", excerpt: "", content: "", image_url: "", category: "Conseils", published: true });
      loadPosts();
    } else {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    const res = await fetch(`${API}/blog/${id}`, { method: "DELETE", headers });
    if (res.ok) { toast.success("Article supprimé"); loadPosts(); }
    else toast.error("Erreur de suppression");
  };

  // Login screen
  if (!token) {
    return (
      <div style={{
        minHeight: "100vh", backgroundColor: "#F7F3EA",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
      }}>
        <div style={{ width: "100%", maxWidth: "400px" }}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <img src={LOGO_CHOCO} alt="ELLAA WEDDING" style={{ height: "60px", margin: "0 auto 20px", display: "block" }} />
            <p style={{ fontSize: "10px", letterSpacing: "5px", textTransform: "uppercase", color: "#6D5E56" }}>
              Espace Administration
            </p>
          </div>

          <form onSubmit={login} data-testid="admin-login-form" style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <div>
              <label style={{ fontSize: "10px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "8px" }}>
                Mot de passe
              </label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="admin-input"
                  data-testid="admin-password"
                  style={{ paddingRight: "44px" }}
                />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#6D5E56" }}>
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={authLoading}
              className="btn-primary"
              style={{ border: "none", cursor: "pointer", width: "100%" }}
            >
              {authLoading ? "Connexion..." : "Se connecter"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#F7F3EA" }} data-testid="admin-panel">

      {/* Admin nav */}
      <header style={{
        backgroundColor: "#3D1602", padding: "0 24px",
        position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", height: "64px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "32px" }}>
            <img src={LOGO_CHOCO} alt="ELLAA WEDDING" style={{ height: "36px", filter: "invert(1) brightness(2) sepia(1) saturate(0)" }} />
            <div style={{ display: "flex", gap: "4px" }}>
              {[
                { key: "gallery", label: "Galerie" },
                { key: "blog", label: "Blog" },
                { key: "contacts", label: "Messages" },
              ].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                  data-testid={`admin-tab-${t.key}`}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    fontSize: "11px", letterSpacing: "3px", textTransform: "uppercase",
                    padding: "8px 16px",
                    color: tab === t.key ? "#E9DCC4" : "rgba(233,220,196,0.5)",
                    borderBottom: tab === t.key ? "2px solid #E9DCC4" : "2px solid transparent",
                    transition: "all 0.3s ease",
                  }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={clear}
            data-testid="admin-logout"
            style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(233,220,196,0.5)", display: "flex", alignItems: "center", gap: "8px", fontSize: "11px", letterSpacing: "2px" }}
          >
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </header>

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "40px 24px" }}>

        {/* GALLERY TAB */}
        {tab === "gallery" && (
          <div data-testid="admin-gallery">
            <h2 style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontSize: "1.8rem", fontWeight: 300, color: "#3D1602", marginBottom: "32px" }}>
              Gestion de la Galerie
            </h2>

            {/* Add photo */}
            <div style={{ border: "1px solid rgba(61,22,2,0.1)", padding: "32px", marginBottom: "40px", backgroundColor: "white" }}>
              <h3 style={{ fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "24px" }}>Ajouter une photo</h3>
              <form onSubmit={addPhoto} data-testid="add-photo-form" style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "16px", alignItems: "end" }}>
                <div>
                  <label style={{ fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "6px" }}>URL de la photo</label>
                  <input type="url" value={newPhoto.url} onChange={e => setNewPhoto(p => ({ ...p, url: e.target.value }))}
                    placeholder="https://..." className="admin-input" data-testid="photo-url" />
                </div>
                <div>
                  <label style={{ fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: "#6D5E56", display: "block", marginBottom: "6px" }}>Titre</label>
                  <input type="text" value={newPhoto.title} onChange={e => setNewPhoto(p => ({ ...p, title: e.target.value }))}
                    placeholder="Ex: Cérémonie élégante" className="admin-input" data-testid="photo-title" />
                </div>
                <button type="submit" data-testid="add-photo-submit" className="btn-primary" style={{ border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap" }}>
                  <Plus size={14} /> Ajouter
                </button>
              </form>
            </div>

            {/* Gallery grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}
              className="sm:grid-cols-3 lg:grid-cols-4">
              {gallery.map(photo => (
                <div key={photo.id} data-testid={`admin-photo-${photo.id}`}
                  style={{ position: "relative", border: "1px solid rgba(61,22,2,0.1)" }}>
                  <img src={photo.url} alt={photo.title} style={{ width: "100%", height: "200px", objectFit: "cover", display: "block" }} />
                  <div style={{ padding: "12px", backgroundColor: "white" }}>
                    <p style={{ fontSize: "12px", color: "#3D1602", fontWeight: 400, marginBottom: "4px" }}>{photo.title}</p>
                    <p style={{ fontSize: "10px", letterSpacing: "2px", color: "#6D5E56", textTransform: "uppercase", marginBottom: "12px" }}>{photo.category}</p>
                    <button onClick={() => deletePhoto(photo.id)}
                      data-testid={`delete-photo-${photo.id}`}
                      style={{ background: "none", border: "1px solid rgba(61,22,2,0.2)", cursor: "pointer", padding: "6px 12px", display: "flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "#6D5E56", letterSpacing: "1px" }}>
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BLOG TAB */}
        {tab === "blog" && (
          <div data-testid="admin-blog">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
              <h2 style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontSize: "1.8rem", fontWeight: 300, color: "#3D1602" }}>
                Gestion du Blog
              </h2>
              <button onClick={() => { setShowNewPost(true); setEditPost(null); }}
                data-testid="new-post-btn"
                className="btn-primary" style={{ border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}>
                <Plus size={14} /> Nouvel article
              </button>
            </div>

            {/* New/Edit post form */}
            {(showNewPost || editPost) && (
              <div style={{ border: "1px solid rgba(61,22,2,0.15)", padding: "32px", marginBottom: "40px", backgroundColor: "white" }}>
                <h3 style={{ fontSize: "12px", letterSpacing: "3px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "24px" }}>
                  {editPost ? "Modifier l'article" : "Nouvel article"}
                </h3>
                <form onSubmit={savePost} data-testid="post-form" style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <input type="text" placeholder="Titre de l'article" className="admin-input"
                    value={editPost ? editPost.title : newPost.title}
                    onChange={e => editPost ? setEditPost(p => ({ ...p, title: e.target.value })) : setNewPost(p => ({ ...p, title: e.target.value }))}
                    data-testid="post-title" />
                  <input type="text" placeholder="Image URL" className="admin-input"
                    value={editPost ? editPost.image_url : newPost.image_url}
                    onChange={e => editPost ? setEditPost(p => ({ ...p, image_url: e.target.value })) : setNewPost(p => ({ ...p, image_url: e.target.value }))}
                    data-testid="post-image" />
                  <input type="text" placeholder="Extrait (résumé court)" className="admin-input"
                    value={editPost ? editPost.excerpt : newPost.excerpt}
                    onChange={e => editPost ? setEditPost(p => ({ ...p, excerpt: e.target.value })) : setNewPost(p => ({ ...p, excerpt: e.target.value }))}
                    data-testid="post-excerpt" />
                  <textarea placeholder="Contenu HTML de l'article" rows={8} className="admin-input" style={{ resize: "vertical" }}
                    value={editPost ? editPost.content : newPost.content}
                    onChange={e => editPost ? setEditPost(p => ({ ...p, content: e.target.value })) : setNewPost(p => ({ ...p, content: e.target.value }))}
                    data-testid="post-content" />
                  <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                    <input type="text" placeholder="Catégorie" className="admin-input" style={{ flex: 1 }}
                      value={editPost ? editPost.category : newPost.category}
                      onChange={e => editPost ? setEditPost(p => ({ ...p, category: e.target.value })) : setNewPost(p => ({ ...p, category: e.target.value }))}
                      data-testid="post-category" />
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#6D5E56", whiteSpace: "nowrap" }}>
                      <input type="checkbox"
                        checked={editPost ? editPost.published : newPost.published}
                        onChange={e => editPost ? setEditPost(p => ({ ...p, published: e.target.checked })) : setNewPost(p => ({ ...p, published: e.target.checked }))}
                      />
                      Publié
                    </label>
                  </div>
                  <div style={{ display: "flex", gap: "12px" }}>
                    <button type="submit" className="btn-primary" style={{ border: "none", cursor: "pointer" }} data-testid="save-post-btn">
                      {editPost ? "Enregistrer" : "Créer l'article"}
                    </button>
                    <button type="button" onClick={() => { setEditPost(null); setShowNewPost(false); }} className="btn-outline-dark" style={{ cursor: "pointer" }}>
                      Annuler
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Posts list */}
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {posts.map(post => (
                <div key={post.id} data-testid={`admin-post-${post.id}`}
                  style={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px 20px", backgroundColor: "white", border: "1px solid rgba(61,22,2,0.08)" }}>
                  <img src={post.image_url} alt={post.title} style={{ width: "64px", height: "64px", objectFit: "cover", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: "14px", color: "#3D1602", fontWeight: 400, marginBottom: "4px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{post.title}</p>
                    <p style={{ fontSize: "10px", letterSpacing: "2px", color: "#6D5E56", textTransform: "uppercase" }}>
                      {post.category} · {post.published ? "Publié" : "Brouillon"}
                    </p>
                  </div>
                  <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
                    <button onClick={() => { setEditPost(post); setShowNewPost(false); }}
                      data-testid={`edit-post-${post.id}`}
                      style={{ background: "none", border: "1px solid rgba(61,22,2,0.2)", cursor: "pointer", padding: "6px 12px", display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#6D5E56", letterSpacing: "1px" }}>
                      <Edit size={12} /> Modifier
                    </button>
                    <button onClick={() => deletePost(post.id)}
                      data-testid={`delete-post-${post.id}`}
                      style={{ background: "none", border: "1px solid rgba(61,22,2,0.2)", cursor: "pointer", padding: "6px 12px", display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#6D5E56", letterSpacing: "1px" }}>
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACTS TAB */}
        {tab === "contacts" && (
          <div data-testid="admin-contacts">
            <h2 style={{ fontFamily: "'Noto Serif Ethiopic', Georgia, serif", fontSize: "1.8rem", fontWeight: 300, color: "#3D1602", marginBottom: "32px" }}>
              Messages reçus ({contacts.length})
            </h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              {contacts.length === 0 ? (
                <p style={{ color: "#6D5E56", padding: "40px 0" }}>Aucun message pour l'instant.</p>
              ) : contacts.map(c => (
                <div key={c.id} data-testid={`contact-${c.id}`}
                  style={{ padding: "24px", backgroundColor: "white", border: "1px solid rgba(61,22,2,0.08)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px", gap: "16px", flexWrap: "wrap" }}>
                    <div>
                      <p style={{ fontSize: "15px", fontWeight: 400, color: "#3D1602", marginBottom: "4px" }}>{c.name}</p>
                      <a href={`mailto:${c.email}`} style={{ fontSize: "12px", color: "#6D5E56", textDecoration: "none" }}>{c.email}</a>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      {c.wedding_date && (
                        <p style={{ fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", color: "#6D5E56", marginBottom: "4px" }}>
                          Mariage : {c.wedding_date}
                        </p>
                      )}
                      <p style={{ fontSize: "10px", color: "rgba(61,22,2,0.4)", letterSpacing: "1px" }}>
                        {new Date(c.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  </div>
                  <p style={{ fontSize: "13px", lineHeight: "1.7", color: "#6D5E56", fontWeight: 300, borderTop: "1px solid rgba(61,22,2,0.06)", paddingTop: "12px" }}>
                    {c.message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
